/**
 * Deduct-on-brew: turning a batch into the stock movements it caused.
 *
 * ## One plan, one guard, one undo step
 *
 * `planInventoryDeduction` reads a batch's **frozen `recipeSnapshot`** — not the
 * live recipe, which may have moved on — allocates it against the cupboard with
 * the same allocator the availability check uses, and returns the exact list of
 * `inventory.adjustQuantity` payloads that expresses the result. It writes
 * nothing and reads no clock.
 *
 * The caller dispatches those adjustments **plus**
 * `batch.setInventoryDeducted` as one batch, so the whole thing is one entry in
 * the undo stack: ⌘Z puts every gram back *and* clears the guard, because the
 * guard is inverted along with everything else.
 *
 * ## Why double deduction is structurally impossible
 *
 * The guard is not a check the UI is trusted to perform. It is an input to this
 * function: **a batch with `inventoryDeductedAt` set produces an empty plan.**
 * There is nothing to dispatch, so a double-clicked button, a re-render, a
 * second tab and a synced second device all produce the same nothing. The UI's
 * disabled state is a courtesy on top of that, not the mechanism.
 *
 * ## Partial stock
 *
 * A lot that cannot cover a row is emptied rather than skipped, and the
 * remainder is reported as a shortfall. That is what actually happened on brew
 * day — the brewer used what they had and topped up from somewhere — and it
 * leaves the cupboard honest instead of showing 40 g of a hop that is gone.
 */

import type { BatchDoc } from '../document/batch';
import type { InventoryKind, InventoryUnit } from '../document/inventory';
import { roundQuantity } from '../document/inventory';
import type { CustomIngredientTable, InventoryTable } from './availability';
import { allocateRequirements, recipeRequirements } from './availability';

/** The `inventory.adjustQuantity` payload, restated so this module owns no import cycle. */
export interface InventoryAdjustment {
  readonly itemId: string;
  /** Negative: this is stock leaving. In the item's own unit. */
  readonly delta: number;
  readonly reason: string;
}

/** One lot that will be drawn down, for the "here is what was taken" readout. */
export interface DeductionLine {
  readonly inventoryItemId: string;
  readonly kind: InventoryKind;
  /** The stocked lot's name, which is what the brewer will recognise. */
  readonly name: string;
  readonly unit: InventoryUnit;
  /** How much leaves this lot, **positive**. */
  readonly quantity: number;
}

/** An ingredient the cupboard could not fully cover. */
export interface DeductionShortfall {
  readonly kind: InventoryKind;
  readonly name: string;
  readonly unit: InventoryUnit;
  readonly required: number;
  readonly held: number;
  readonly shortfall: number;
  /** True when nothing in the cupboard matched it at all. */
  readonly unmatched: boolean;
}

/** What {@link planInventoryDeduction} produces. */
export interface InventoryDeductionPlan {
  readonly batchId: string;
  /** Non-null when this batch has already been deducted; the plan is then empty. */
  readonly alreadyDeductedAt: number | null;
  /** The commands to dispatch, one per drawn-down lot. Empty when guarded. */
  readonly adjustments: readonly InventoryAdjustment[];
  /** The same movements, grouped for display. Empty when guarded. */
  readonly lines: readonly DeductionLine[];
  /** Ingredients the cupboard could not cover. Empty when guarded. */
  readonly shortfalls: readonly DeductionShortfall[];
  /** True when there is at least one adjustment to dispatch. */
  readonly deductible: boolean;
}

/** Options for {@link planInventoryDeduction}. */
export interface DeductionOptions {
  /**
   * What every adjustment records as its reason. Defaults to
   * `Brewed "<batch name>"`.
   */
  readonly reason?: string;
  /** The workspace's custom-ingredient table, for the link rung; see `checkRecipeAvailability`. */
  readonly customIngredients?: CustomIngredientTable | null;
}

/** The reason string a deduction records when the caller names none. */
export function defaultDeductionReason(batch: BatchDoc): string {
  return `Brewed "${batch.name}"`.slice(0, 200);
}

/**
 * Plan the stock movements one batch causes.
 *
 * @param batch - The batch. Its `recipeSnapshot` is the demand, and its
 *   `inventoryDeductedAt` is the guard.
 * @param inventory - The workspace's `inventory` table.
 */
export function planInventoryDeduction(
  batch: BatchDoc,
  inventory: InventoryTable,
  options: DeductionOptions = {},
): InventoryDeductionPlan {
  if (batch.inventoryDeductedAt !== null) {
    return {
      batchId: batch.id,
      alreadyDeductedAt: batch.inventoryDeductedAt,
      adjustments: [],
      lines: [],
      shortfalls: [],
      deductible: false,
    };
  }

  const reason = (options.reason ?? defaultDeductionReason(batch)).slice(0, 200);
  const customIngredients = options.customIngredients ?? null;
  const allocated = allocateRequirements(
    recipeRequirements(batch.recipeSnapshot, customIngredients),
    inventory,
    customIngredients,
  );

  // Two rows of the same recipe can draw on the same lot (a bittering and a
  // whirlpool addition of one hop), and one command per lot is both fewer
  // commands to sync and a single, readable undo entry.
  const byLot = new Map<string, DeductionLine>();
  const shortfalls: DeductionShortfall[] = [];

  for (const line of allocated) {
    for (const allocation of line.allocations) {
      const lot = inventory[allocation.inventoryItemId];
      if (lot === undefined) continue;
      const existing = byLot.get(lot.id);
      byLot.set(lot.id, {
        inventoryItemId: lot.id,
        kind: lot.kind,
        name: lot.name,
        unit: line.unit,
        quantity: roundQuantity((existing?.quantity ?? 0) + allocation.quantity),
      });
    }
    if (line.shortfall > 0) {
      shortfalls.push({
        kind: line.kind,
        name: line.name,
        unit: line.unit,
        required: line.required,
        held: line.held,
        shortfall: line.shortfall,
        unmatched: line.status === 'missing',
      });
    }
  }

  const lines = [...byLot.values()].filter((line) => line.quantity > 0);
  const adjustments = lines.map<InventoryAdjustment>((line) => ({
    itemId: line.inventoryItemId,
    delta: roundQuantity(-line.quantity),
    reason,
  }));

  return {
    batchId: batch.id,
    alreadyDeductedAt: null,
    adjustments,
    lines,
    shortfalls,
    deductible: adjustments.length > 0,
  };
}
